import { PaginatedDataDTO, PaginationDTO } from '@bottega52/commons-pagination';
import { TextField, WithStyles } from '@material-ui/core';
import { createStyles, withStyles } from '@material-ui/core/styles';
import { SearchOutlined } from '@material-ui/icons';
import { Stack } from '@mui/material';
import { DataGrid, GridColumns, GridRenderCellParams } from '@mui/x-data-grid';
import _ from 'lodash';
import React from 'react';
import { IMechanicalCylinderKeyRelationInDTO } from '../../repository/jago/model/input/IMechanicalCylinderKeyRelationInDTO';
import translations from '../../translations/i18next';
import ReduxLanguage from '../ReduxLanguage';

const styles = createStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: 0,
  },
  searchContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: 10,
    marginBottom: 10,
    padding: 10,
    backgroundColor: 'white',
    borderRadius: 4,
  },
  tableContainer: {
    display: 'flex',
    backgroundColor: 'white',
    height: 400,
    width: '100%'
  }
});

export interface IAssociatedKeysTableProps extends WithStyles<typeof styles> {
  relations: PaginatedDataDTO<IMechanicalCylinderKeyRelationInDTO>;
  onFetchElementsOnPage: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onSearch: (keyFunction: string, keyDesignation: string) => void;
  onRowClick?: (keyData: any) => void;
  rowsPerPage: number;
  isLoading?: boolean;
  useClientSideFiltering?: boolean;
}

export interface IAssociatedKeysTableState {
  keyFunctionFilter: string;
  keyDesignationFilter: string;
}

class AssociatedKeysTable extends React.Component<IAssociatedKeysTableProps, IAssociatedKeysTableState> {
  private keysColumns: GridColumns;
  private debouncedSearch = _.debounce(() => {
    const { keyFunctionFilter, keyDesignationFilter } = this.state;
    this.props.onSearch(keyFunctionFilter, keyDesignationFilter);
  }, 500);

  constructor(props: IAssociatedKeysTableProps) {
    super(props);
    this.state = {
      keyFunctionFilter: '',
      keyDesignationFilter: '',
    };

    this.keysColumns = [
      {
        field: 'keyFunction',
        headerName: translations.t('mks.keyFunction'),
        type: 'string',
        sortable: false,
        filterable: false,
        minWidth: 200,
        flex: 2,
        renderCell: (params: GridRenderCellParams<any>) => {
          const relation = params.row;
          return <h3 style={{ marginLeft: 0, fontWeight: 'bold' }}>{relation.key.keyFunction || 'N/A'}</h3>
        }
      },
      {
        field: 'designation',
        headerName: translations.t('mks.designation'),
        type: 'string',
        sortable: false,
        filterable: false,
        minWidth: 200,
        flex: 2,
        renderCell: (params: GridRenderCellParams<any>) => {
          return params.row.key.designation || 'N/A';
        }
      },
      {
        field: 'externalId',
        headerName: translations.t('mks.externalId'),
        type: 'number',
        sortable: false,
        filterable: false,
        minWidth: 100,
        flex: 1,
        renderCell: (params: GridRenderCellParams<any>) => {
          return params.row.key.externalId || 'N/A';
        }
      },
      {
        field: 'quantity',
        headerName: translations.t('mks.quantity'),
        type: 'number',
        sortable: false,
        filterable: false,
        minWidth: 100,
        flex: 1,
        renderCell: (params: GridRenderCellParams<any>) => {
          return params.row.key.quantity || 0;
        }
      },
    ];
  }

  onFilterChange(field: 'keyFunctionFilter' | 'keyDesignationFilter', value: string) {
    this.setState({ [field]: value } as any, () => {
      if (this.props.useClientSideFiltering) {
        this.props.onSearch(this.state.keyFunctionFilter, this.state.keyDesignationFilter);
      } 
      else {
        this.debouncedSearch();
      }
    });
  }

  public render() {
    const { classes, relations, rowsPerPage, isLoading, onFetchElementsOnPage, onPageSizeChange, onRowClick, useClientSideFiltering } = this.props;
    const { keyFunctionFilter, keyDesignationFilter } = this.state;

    const hasRelations = relations && relations.content && relations.content.length > 0;

    return (
      <div className={classes.container}>
        <div className={classes.searchContainer}>
          <TextField
            label={<ReduxLanguage languageKey="mks.filterByKeyFunction" />}
            variant="outlined"
            size="small"
            value={keyFunctionFilter}
            onChange={(e) => this.onFilterChange('keyFunctionFilter', e.target.value)}
            style={{ minWidth: 200 }}
          />
          <TextField
            label={<ReduxLanguage languageKey="mks.filterByDesignation" />}
            variant="outlined"
            size="small"
            value={keyDesignationFilter}
            onChange={(e) => this.onFilterChange('keyDesignationFilter', e.target.value)}
            style={{ minWidth: 200 }}
          />
        </div>

        <div className={classes.tableContainer}>
          <DataGrid
            getRowId={(row) => row.id}
            rows={relations.content || []}
            columns={this.keysColumns}
            rowHeight={50}
            loading={isLoading}
            onPageSizeChange={onPageSizeChange}
            onPageChange={onFetchElementsOnPage}
            onRowClick={onRowClick ? (params) => onRowClick(params.row.key) : undefined}
            page={(relations?.pagination as PaginationDTO)?.number}
            rowCount={(relations?.pagination as PaginationDTO)?.totalElements}
            pageSize={rowsPerPage}
            paginationMode="server"
            disableColumnMenu
            disableColumnSelector
            componentsProps={{
              pagination: {
                labelRowsPerPage: translations.t('forms.rowsPerPage')
              }
            }}
            components={{
              NoRowsOverlay: () => (
                <Stack height="100%" alignItems="center" justifyContent="center">
                  <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center'}}>
                    <SearchOutlined style={{ alignSelf: 'center' }} />
                    <h3 style={{ textAlign: 'center', fontWeight: 'bold', marginBottom: 0 }}>
                      <ReduxLanguage languageKey='mks.noAssociatedKeys' />
                    </h3>
                  </div>
                </Stack>
              ),
              NoResultsOverlay: () => (
                <Stack height="100%" alignItems="center" justifyContent="center">
                  <h3 style={{ textAlign: 'center', fontWeight: 'bold', marginBottom: 0 }}>
                    <ReduxLanguage languageKey='mks.noAssociatedKeys' />
                  </h3>
                </Stack>
              )
            }}
            sx={{
              '& .MuiDataGrid-cell:focus': {
                outline: 'none',
              },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#f5f5f5',
              },
            }}
          />
        </div>
      </div>
    );
  }
}

export default withStyles(styles)(AssociatedKeysTable);
